import React, { useState } from "react";
import axios from "axios";
import { ChevronDown } from "lucide-react";
import financial_advisory from "../assets/img/financial_advisory.png";
import InsightIcon from "../assets/icons/thinkIcons/insightIcon.png";
import InfoIcon from "../assets/icons/thinkIcons/info.png";
import HomeIcon from "../assets/icons/thinkIcons/Home.png";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";

const FinancialAdvisoryTool = () => {
  const navigate = useNavigate();
  const [income, setIncome] = useState("");
  const [expenses, setExpenses] = useState("");
  const [risk, setRisk] = useState("Low");
  const [symbol, setSymbol] = useState("");
  const [advice, setAdvice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showMarketInsight, setShowMarketInsight] = useState(false);
  const [marketInsight, setMarketInsight] = useState("");
  const [loadingInsight, setLoadingInsight] = useState(false);

  const handleMouseClick = () => {
    navigate("/user/dashboard");
  };

  const fetchAdvice = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/finance/financial-advice`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            income: parseFloat(income),
            expenses: parseFloat(expenses),
            risk: risk,
          }),
        }
      );
      const data = await response.json();
      setAdvice(data);
    } catch (error) {
      console.error("Error fetching advice:", error);
      setAdvice({ error: "Failed to get advice. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const handleMarketInsight = async (e) => {
    if (e.key === "Enter" && symbol.trim()) {
      setLoadingInsight(true);
      setMarketInsight(null); // Clear old insight

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/finance/market-insight`,
          { symbol: symbol.toUpperCase() }
        );

        // Ensure object format
        const insightData = response.data?.insight;
        if (insightData && typeof insightData === "object") {
          setMarketInsight(insightData);
        } else {
          setMarketInsight({ error: "Invalid insight data format received." });
        }
      } catch (error) {
        console.error("Error fetching market insight:", error);
        setMarketInsight({ error: "Failed to fetch insight. Try again." });
      } finally {
        setLoadingInsight(false);
      }
    }
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 lg:p-10">
      {/* Header Row */}
      <div className="flex justify-end">
        <button
          className="flex items-center gap-2 border px-3 py-2 rounded-full"
          onClick={handleMouseClick}
        >
          <img src={HomeIcon} className="w-4 h-4 sm:w-5 sm:h-5" />
          <span className="text-xs sm:text-sm">Home</span>
        </button>
      </div>

      {/* Top Card */}
      <div className="border-1 rounded-md mt-4 flex flex-col sm:flex-row p-3 gap-4 border-blue-custom">
        <img
          src={financial_advisory}
          alt="Advisory Tool"
          className="w-full sm:w-[200px] h-[180px] sm:h-[120px] object-cover rounded-md"
        />
        <div className="">
          <h2 className="textcolor font-semibold text-lg sm:text-xl">
            Financial Advisory Tool
          </h2>
          <p className="textsec text-sm sm:text-base">
            Personalized financial guidance tailored to your profile.
          </p>
        </div>
      </div>

      {/* Input Section */}
      <div className="mt-6 pt-4 border-gray-300">
        <p className="textcolor font-semibold text-sm sm:text-base border-l-4 pl-2 border-blue-custom">
          Enter your Details to Receive Instant Guidance
        </p>
        <p className="border-b-2"></p>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-6">
          {/* Monthly Income */}
          <div>
            <label className="block textblack text-sm font-semibold mb-1">
              Monthly Income
            </label>
            <input
              type="number"
              placeholder="Enter your Monthly Income"
              value={income}
              onChange={(e) => setIncome(e.target.value)}
              className="w-full h-[52px] sm:h-[62px] px-3 text-sm border-1 border-blue-custom rounded-md focus:outline-none placeholder:text-gray-400"
            />
          </div>

          {/* Monthly Expenses */}
          <div>
            <label className="block textblack text-sm font-semibold mb-1">
              Monthly Expenses
            </label>
            <input
              type="number"
              placeholder="Enter your Monthly Expenses"
              value={expenses}
              onChange={(e) => setExpenses(e.target.value)}
              className="w-full h-[52px] sm:h-[62px] px-3 text-sm border-1 border-blue-custom rounded-md focus:outline-none placeholder:text-gray-400"
            />
          </div>

          {/* Risk Appetite */}
          <div>
            <label className="block textblack text-sm font-semibold mb-1">
              Risk Appetite
            </label>
            <div className="relative">
              <select
                value={risk}
                onChange={(e) => setRisk(e.target.value)}
                className="w-full h-[52px] sm:h-[62px] px-3 text-sm border-1 border-blue-custom rounded-md appearance-none focus:outline-none"
              >
                <option>Low</option>
                <option>Medium</option>
                <option>High</option>
              </select>
              <ChevronDown
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none"
                size={18}
              />
            </div>
          </div>
        </div>

        <div className="mt-8 sm:mt-10 flex justify-center">
          <button
            className="bgbtn textwhite px-6 sm:px-8 py-2 rounded-md shadow-md hover:opacity-90"
            onClick={fetchAdvice}
            disabled={loading}
          >
            {loading ? "Fetching..." : "Get Advice"}
          </button>
        </div>
      </div>

      {/* Advice Box */}
      {advice && (
        <div className="mt-8 border border-blue-custom rounded-md p-4 bg-white shadow-sm">
          <h3 className="textcolor font-semibold mb-4 text-base sm:text-lg">
            Your Financial Advice
          </h3>

          {advice.error ? (
            <p className="text-red-600">{advice.error}</p>
          ) : (
            <div className="prose prose-sm max-w-none textblack">
              <ReactMarkdown>{advice.advice}</ReactMarkdown>
            </div>
          )}
        </div>
      )}

      <div className="mb-4">
        {/* Market Insight Button */}
        <div>
          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <button
              className="flex items-center gap-3 border-1 border-blue-custom px-4 py-2 rounded-md"
              onClick={() => setShowMarketInsight(true)}
            >
              <img src={InsightIcon} className="w-6 sm:w-7" />
              <span className="text-sm sm:text-base">Get Market Insight</span>
            </button>
            <img
              src={InfoIcon}
              className="cursor-pointer w-4 sm:w-5"
              title="Info"
            />
          </div>

          {showMarketInsight && (
            <>
              <p className="textcolor font-semibold text-sm sm:text-base border-l-4 pl-2 border-blue-custom mb-3 mt-6">
                Market Insight
              </p>

              <div className="mb-4">
                <label className="block textblack text-sm font-semibold mb-1">
                  Enter Stock Ticker Symbol (e.g., AAPL, TSLA)
                </label>
                <input
                  type="text"
                  placeholder="TSLA"
                  value={symbol}
                  onChange={(e) => setSymbol(e.target.value)}
                  onKeyDown={handleMarketInsight}
                  className="w-full h-[40px] sm:h-[42px] px-3 text-sm border border-blue-custom rounded-md focus:outline-none"
                />
              </div>

              {marketInsight && (
                <div className="border border-blue-custom rounded-md p-3 text-sm textblack leading-relaxed">
                  {loadingInsight ? (
                    <p className="text-blue-600 font-semibold">
                      Loading market insight...
                    </p>
                  ) : marketInsight.error ? (
                    <p className="text-red-600">{marketInsight.error}</p>
                  ) : (
                    <div className="space-y-4">
                      {Object.entries(marketInsight)
                        .slice(0, 5)
                        .map(([date, stats]) => (
                          <div
                            key={date}
                            className="border border-gray-200 rounded-md p-3 shadow-sm hover:shadow-md transition duration-200"
                          >
                            <h4 className="font-bold text-blue-custom mb-2">
                              {date}
                            </h4>
                            <ul className="list-disc pl-5 space-y-1">
                              {Object.entries(stats).map(([key, value]) => (
                                <li key={key}>
                                  <span className="font-semibold">
                                    {key.replace(/^\d+\.\s*/, "")}:
                                  </span>{" "}
                                  {value}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinancialAdvisoryTool;
